import React, { MouseEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import _ from 'lodash';
import { OrderDirection } from '../Datatable/OrderDirection';
import { invalidTonnageSelector, setCorrectInvalidTonnageParam, setInvalidTonnageData, setInvalidTonnageDisplay, setInvalidTonnageOrderBy, setInvalidTonnageOrderDirection, setInvalidTonnageSearchText, setOpenEditInvalidTonnageModal, setRefreshInvalidTonnageTable, setSelectedItem } from './reducers/InvalidTonnageReducer';
import { InvalidTonnageModel } from './models/InvalidTonnageModel';
import moment from 'moment';
import { Box, Button, Grid, Modal } from '@mui/material';
import TonnageApi from '../Tonnage/api/TonnageApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import PortSelector from '../PortSelector/PortSelector';
import PortModel from '../PortSelector/models/PortModel';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import ContactSelector from '../ContactSelector/ContactSelector';
import TonnageModel from '../Tonnage/models/TonnageModel';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';

const InvalidTonnageTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let invalidTonnageData = useSelector(invalidTonnageSelector);
    let portSelectorData = useSelector(portSelectorSelector);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "800px",
        bgcolor: 'white',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    useEffect(() => {
        TonnageApi.GetInvalidTonnages()
            .then((response: BaseResponse<InvalidTonnageModel[]>) => {
                if (response.success) {
                    dispatch(setInvalidTonnageDisplay(response.data));
                    dispatch(setInvalidTonnageData(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
    }, [invalidTonnageData.refreshInvalidTonnageTable])

    const columns = [
        {
            Field: "vesselName",
            Title: "Vessel Name",
            AllowSort: true,
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "dwt",
            Title: "DWT",
            AllowSort: true,
            Render: (rowData: InvalidTonnageModel) => {
                return <>{rowData.dwt.toLocaleString()}</>;
            }
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "yearBuilt",
            Title: "BLT",
            AllowSort: true,
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "portName",
            Title: "Port",
            AllowSort: true,
            Render: (rowData: InvalidTonnageModel) => (
                <>
                    {rowData.portActive ? rowData.portName : <span className='invalid-cell'>{rowData.portName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "dateOpenStart",
            Title: "Date",
            AllowSort: true,
            Render: (rowData: InvalidTonnageModel) => (
                <>
                    {formatDateMMMDD(rowData.dateOpenStart).toUpperCase() + " - " + formatDateMMMDD(rowData.dateOpenEnd).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "headOwnerName",
            Title: "Head Owner",
            AllowSort: true,
            Render: (rowData: InvalidTonnageModel) => (
                <>
                    {rowData.headOwnerActive ? rowData.headOwnerName : <span className='invalid-cell'>{rowData.headOwnerName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "operatorName",
            Title: "Operator",
            AllowSort: true,
            Render: (rowData: InvalidTonnageModel) => (
                <>
                    {rowData.operatorActive ? rowData.operatorName : <span className='invalid-cell'>{rowData.operatorName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidTonnageModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: InvalidTonnageModel) => (
                <>
                    <Button variant='outlined'
                        onClick={() => {
                            dispatch(setCorrectInvalidTonnageParam({
                                id: rowData.id,
                                portOpen: rowData.portActive ? rowData.portOpen : 0,
                                headOwner: rowData.headOwnerActive ? rowData.headOwner : "",
                                operator: rowData.operatorActive ? rowData.operator : "",
                            }))
                            dispatch(setSelectedItem(rowData));
                            dispatch(setOpenEditInvalidTonnageModal(true));
                        }}
                    >
                        Correct
                    </Button>
                </>
            )
        } as DatatableColumnModel<InvalidTonnageModel>,
    ];

    const formatDateMMMDD = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD MMM");
    };

    const getTableClassName = () => {
        let className = "cargo-type-management-datatable-search-result nice-scroll";
        return className;
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    return <div className='cargo-type-datatable-wrapper'>
        <Datatable<InvalidTonnageModel>
            useDefaultSearchBar={true}
            onSearchValueChange={(value: string) => {
                dispatch(setInvalidTonnageSearchText(value));
                let data = _.filter([...invalidTonnageData.invalidTonnageData], (item) => {
                    return item.vesselName.indexOf(value.toUpperCase()) > -1;
                })
                dispatch(setInvalidTonnageDisplay(data));
            }}
            pagingType={PagingTypeEnum.None}
            data={invalidTonnageData.invalidTonnageDisplay}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<InvalidTonnageModel>, orderDirection: OrderDirection) => {
                dispatch(setInvalidTonnageOrderBy(column.Field));
                dispatch(setInvalidTonnageOrderDirection(orderDirection));

                let data = [...invalidTonnageData.invalidTonnageDisplay];
                if (orderDirection == OrderDirection.Asc) {
                    data = _.sortBy(data, column.Field);
                } else {
                    data = _.sortBy(data, column.Field).reverse();
                }

                dispatch(setInvalidTonnageDisplay(data));
            }}
            orderColumn={invalidTonnageData.orderBy}
            orderDirection={invalidTonnageData.orderDirection}
            stickyHeader={true}
        ></Datatable>
        <Modal
            open={invalidTonnageData.openEditInvalidTonnageModal}
            onClose={() => {
                dispatch(setOpenEditInvalidTonnageModal(false));
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                {
                    invalidTonnageData.selectedItem &&
                    <Grid container>
                        {
                            !invalidTonnageData.selectedItem.portActive &&
                            <Grid item xs={12} className="input-component">
                                <PortSelector
                                    onPortChange={(value: PortModel | null) => {
                                        if (value) {
                                            dispatch(setCorrectInvalidTonnageParam({
                                                ...invalidTonnageData.editItemParam,
                                                portOpen: (value as PortModel).id
                                            }));
                                        } else {
                                            dispatch(setCorrectInvalidTonnageParam({
                                                ...invalidTonnageData.editItemParam,
                                                portOpen: 0
                                            }));
                                        }
                                    }}
                                    portLabel='Port Open'
                                    areaLabel='Area'
                                    isAreaDisabled={true}
                                    portValue={getPortById(invalidTonnageData.editItemParam.portOpen)}
                                    isAreaRelatedToPort={true}
                                >
                                </PortSelector>
                            </Grid>
                        }
                        {
                            !invalidTonnageData.selectedItem.operatorActive &&
                            <Grid item xs={12} className="input-component">
                                <ContactSelector
                                    label='Operator'
                                    selectedContactId={invalidTonnageData.editItemParam.operator}
                                    onContactIdChange={(value: string) => {
                                        dispatch(setCorrectInvalidTonnageParam({
                                            ...invalidTonnageData.editItemParam,
                                            operator: value
                                        }));
                                    }}
                                >
                                </ContactSelector>
                            </Grid>
                        }
                        {
                            !invalidTonnageData.selectedItem.headOwnerActive &&
                            <Grid item xs={12} className="input-component">
                                <ContactSelector
                                    label='Head Owner'
                                    selectedContactId={invalidTonnageData.editItemParam.headOwner}
                                    onContactIdChange={(value: string) => {
                                        dispatch(setCorrectInvalidTonnageParam({
                                            ...invalidTonnageData.editItemParam,
                                            headOwner: value
                                        }));
                                    }}
                                >
                                </ContactSelector>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={9}>
                                </Grid>
                                <Grid item xs={3}>
                                    <Button
                                        variant='contained'
                                        fullWidth
                                        onClick={() => {
                                            if (invalidTonnageData.selectedItem && !invalidTonnageData.selectedItem.portActive && invalidTonnageData.editItemParam.portOpen === 0) {
                                                Utility.Alert.Success("Port open is required");
                                                return;
                                            }

                                            dispatch(showLoading(true));
                                            TonnageApi.CorrectInvalid(invalidTonnageData.editItemParam)
                                                .then((response: BaseResponse<TonnageModel>) => {
                                                    if (response.success) {

                                                        Utility.Alert.Success("Tonnage updated successful");

                                                        dispatch(showLoading(false));

                                                        dispatch(setRefreshInvalidTonnageTable((new Date()).getTime()))

                                                        dispatch(setOpenEditInvalidTonnageModal(false));
                                                    }
                                                })
                                        }}
                                    >Save</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Box>
        </Modal>
    </div>
};

export default InvalidTonnageTable;